/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { DisableGeolocation, EnableGeolocation, MapCreated, MapDestroyed } from '../state/active-map.actions';
import { ActiveMapState } from '../state/active-map.state';
import { LoggerService } from '../../shared/services/logger.service';

@Injectable()
export class LocationService {
    private positionSubject = new Subject<GeolocationPosition>();
    private watcherId: number;

    constructor(private logger: LoggerService, private actions$: Actions, private store: Store) {
        this.actions$.pipe(ofActionSuccessful(MapCreated)).subscribe(() => {
            if (navigator.geolocation) {
                this.watcherId = this.watchPosition();
            }
        });

        this.actions$.pipe(ofActionSuccessful(MapDestroyed)).subscribe(() => {
            if (this.watcherId) {
                navigator.geolocation.clearWatch(this.watcherId);
                this.watcherId = null;
            }
        });
    }

    private watchPosition(): number {
        return navigator.geolocation.watchPosition(
            // location callback
            (position: GeolocationPosition) => {
                const enabled = this.store.selectSnapshot(ActiveMapState.geolocationEnabled);
                if (!enabled) {
                    this.store.dispatch(new EnableGeolocation());
                }
                this.positionSubject.next(position);
            },
            (error: GeolocationPositionError) => this.handleError(error),
            {
                enableHighAccuracy: true,
                maximumAge: 1000, // 60 sec (maximum cache age)
            }
        );
    }

    private handleError(error: GeolocationPositionError) {
        this.watcherId = null;
        this.logger.error('Geolocation Error', error.message);
        switch (error.code) {
            case error.PERMISSION_DENIED:
                this.store.dispatch(new DisableGeolocation());
                break;
            case error.POSITION_UNAVAILABLE:
                this.store.dispatch(new DisableGeolocation());
                break;
            case error.TIMEOUT:
                this.store.dispatch(new DisableGeolocation());
                break;
        }
    }

    get position$(): Observable<GeolocationPosition> {
        return this.positionSubject.asObservable();
    }
}
